/**
 * Checkout billing form helpers — pre-fill, structured billTo payloads, savedBillingInfo.
 */

/** RefReps issuer block on portal estimates/invoices (From section). */
export const REFREPS_BILLING_FROM = {
  companyName: 'RefReps, LLC',
  line1: '11589 Weeping Willow Dr',
  city: 'Zionsville',
  state: 'IN',
  zip: '46077',
  email: 'support@refreps.com',
  website: 'www.refreps.com',
}

/** Street + city/state/zip lines for the From section. */
export function refRepsBillingFromAddressLines() {
  const { line1, city, state, zip } = REFREPS_BILLING_FROM
  const cityStateZip = [city, state].filter(Boolean).join(', ')
  const tail = [cityStateZip, zip].filter(Boolean).join(' ')
  return [line1, tail].filter(Boolean)
}

/** US ZIP: 12345 or 12345-6789 */
const US_ZIP_PATTERN = '\\d{5}(?:-\\d{4})?'

/** Optional trailing US country suffix after a parsed ZIP (US-only parsing). */
const US_COUNTRY_SUFFIX_PATTERN =
  '(?:,\\s*(?:USA|US|U\\.S\\.A\\.|United States(?:\\s+of\\s+America)?))?\\s*'

/**
 * Parse a single-line US address: `{street}, {city}, {ST} {zip}[, {country}]`.
 * Returns null when the string does not match (incl. no ZIP / non-US).
 * @param {string} raw
 * @returns {{ line1: string, city: string, state: string, zip: string } | null}
 */
export function parseUsCombinedAddress(raw) {
  const s = String(raw ?? '').trim()
  if (!s) return null

  const withCommaZip = new RegExp(
    `^(.+?),\\s*([^,]+),\\s*([A-Za-z]{2})\\s*,\\s*(${US_ZIP_PATTERN})${US_COUNTRY_SUFFIX_PATTERN}$`,
    'i'
  )
  const commaMatch = s.match(withCommaZip)
  if (commaMatch) {
    return {
      line1: commaMatch[1].trim(),
      city: commaMatch[2].trim(),
      state: commaMatch[3].toUpperCase(),
      zip: commaMatch[4],
    }
  }

  const standard = new RegExp(
    `^(.+?),\\s*([^,]+),\\s*([A-Za-z]{2})\\s+(${US_ZIP_PATTERN})${US_COUNTRY_SUFFIX_PATTERN}$`,
    'i'
  )
  const standardMatch = s.match(standard)
  if (standardMatch) {
    return {
      line1: standardMatch[1].trim(),
      city: standardMatch[2].trim(),
      state: standardMatch[3].toUpperCase(),
      zip: standardMatch[4],
    }
  }

  return null
}

/**
 * Rebuild `{line1}, {city}, {state} {zip}` — never appends country.
 * @param {{ line1?: string, city?: string, state?: string, zip?: string }} parts
 */
function formatBillToAddressFromParts(parts) {
  const line1 = String(parts?.line1 ?? '').trim()
  const city = String(parts?.city ?? '').trim()
  const state = String(parts?.state ?? '').trim()
  const zip = String(parts?.zip ?? '').trim()
  const cityStateZip = [city, state].filter(Boolean).join(', ')
  const tail = [cityStateZip, zip].filter(Boolean).join(' ')
  if (line1 && tail) return `${line1}, ${tail}`
  return line1 || tail
}

/**
 * Normalize bill-to address parts on load, display, and save.
 * Structured city/state/zip win; otherwise parse combined US strings (QuickBooks / legacy).
 * @param {Record<string, unknown> | null | undefined} billTo
 * @returns {{ line1: string, city: string, state: string, zip: string, address: string }}
 */
export function normalizeBillToAddressParts(billTo) {
  const raw = billTo && typeof billTo === 'object' ? billTo : {}
  const addressRaw = String(raw.address ?? '').trim()
  let line1 = String(raw.line1 ?? '').trim()
  let city = String(raw.city ?? '').trim()
  let state = String(raw.state ?? '').trim()
  let zip = String(raw.zip ?? raw.postalCode ?? '').trim()

  const hasStructured = Boolean(city && state && zip)

  if (hasStructured) {
    if (!line1 && addressRaw) {
      line1 = addressRaw
    }
  } else {
    const candidates = [
      addressRaw,
      line1.includes(',') ? line1 : '',
    ].filter(Boolean)

    let parsed = null
    for (const candidate of candidates) {
      parsed = parseUsCombinedAddress(candidate)
      if (parsed) break
    }

    if (parsed) {
      line1 = parsed.line1
      city = parsed.city
      state = parsed.state
      zip = parsed.zip
    } else if (!line1 && addressRaw) {
      line1 = addressRaw
    }
  }

  state = state ? state.toUpperCase() : ''

  const formatted = formatBillToAddressFromParts({ line1, city, state, zip })

  return {
    line1,
    city,
    state,
    zip,
    address: formatted || addressRaw || line1,
  }
}

/**
 * @param {{ line1?: string, city?: string, state?: string, zip?: string, address?: string }} fields
 */
export function formatBillToAddress(fields) {
  const normalized = normalizeBillToAddressParts(fields ?? {})
  const formatted = formatBillToAddressFromParts(normalized)
  if (formatted) return formatted
  return String(fields?.address ?? fields?.line1 ?? '').trim()
}

/**
 * Organization name for portal bill-to — always the organization record name.
 * @param {Record<string, unknown> | null | undefined} org
 */
export function organizationBillToOrgName(org) {
  return String(org?.name ?? '').trim()
}

/**
 * Force bill-to orgName to the organization record (never user-editable in portal).
 * @param {Record<string, unknown>} fields
 * @param {Record<string, unknown> | null | undefined} org
 */
export function withOrganizationBillToOrgName(fields, org) {
  const orgName = organizationBillToOrgName(org)
  if (!orgName) return { ...fields }
  return { ...fields, orgName }
}

/**
 * Default bill-to fields for quotes/invoices.
 * Pre-fill order: organization.savedBillingInfo → org profile → empty.
 * @param {Record<string, unknown> | null | undefined} org
 * @param {Record<string, unknown>} [overrides]
 */
export function buildDocFieldsFromOrganization(org, overrides = {}) {
  const saved =
    org?.savedBillingInfo && typeof org.savedBillingInfo === 'object'
      ? org.savedBillingInfo
      : null

  const first = String(org?.billingContactFirstName ?? '').trim()
  const last = String(org?.billingContactLastName ?? '').trim()
  const orgContactName = [first, last].filter(Boolean).join(' ')

  const line1 = String(saved?.line1 ?? org?.address ?? '').trim()
  const city = String(saved?.city ?? org?.city ?? '').trim()
  const state = String(saved?.state ?? org?.state ?? '').trim()
  const zip = String(saved?.zip ?? org?.zip ?? '').trim()

  const fields = {
    orgName: organizationBillToOrgName(org),
    contactName: String(saved?.contactName ?? orgContactName).trim(),
    contactEmail: String(saved?.contactEmail ?? org?.billingContactEmail ?? '').trim(),
    line1,
    city,
    state,
    zip,
    address: String(
      saved?.address ?? formatBillToAddress({ line1, city, state, zip })
    ).trim(),
    notes: '',
    poNumber: '',
  }

  return { ...fields, ...overrides }
}

/**
 * Structured billTo for API payloads (QuickBooks sales tax).
 * @param {Record<string, unknown>} docFields
 */
export function buildBillToPayload(docFields) {
  const { line1, city, state, zip } = normalizeBillToAddressParts(docFields ?? {})
  return {
    orgName: String(docFields?.orgName ?? '').trim(),
    contactName: String(docFields?.contactName ?? '').trim(),
    contactEmail: String(docFields?.contactEmail ?? '').trim(),
    line1,
    city,
    state,
    zip,
  }
}

/**
 * Parse a single-line US address produced by formatBillToAddress (or QuickBooks).
 * @param {string} raw
 */
export function parseFormattedAddress(raw) {
  const parsed = parseUsCombinedAddress(raw)
  if (parsed) return parsed
  const s = String(raw ?? '').trim()
  return { line1: s, city: '', state: '', zip: '' }
}

export const BILLING_DOCUMENT_ORG_NAME_HINT =
  'Organization name is set on this invoice/estimate and cannot be changed here.'

/**
 * Map API billing document billTo → portal docFields.
 * Uses document.billTo.orgName as-is (may differ from organization.name).
 * @param {Record<string, unknown>} doc
 * @param {Record<string, unknown>} [fallbackDocFields] prior form values when API omits structured fields
 */
export function billToFromDocument(doc, fallbackDocFields = null) {
  const billTo = doc?.billTo && typeof doc.billTo === 'object' ? doc.billTo : {}
  const normalized = normalizeBillToAddressParts(billTo)
  let { line1, city, state, zip, address } = normalized

  if (
    fallbackDocFields &&
    (!city || !state || !zip) &&
    String(fallbackDocFields.city ?? '').trim()
  ) {
    const fromDoc = formatBillToAddress({ line1, city, state, zip, address })
    const fromFallback = formatBillToAddress(fallbackDocFields)
    if (fromDoc && fromFallback && fromDoc === fromFallback) {
      line1 = String(fallbackDocFields.line1 ?? line1).trim()
      city = String(fallbackDocFields.city ?? city).trim()
      state = String(fallbackDocFields.state ?? state).trim()
      zip = String(fallbackDocFields.zip ?? zip).trim()
      address = formatBillToAddress({ line1, city, state, zip })
    }
  }

  return {
    orgName: String(billTo.orgName ?? '').trim(),
    contactName: String(billTo.contactName ?? '').trim(),
    contactEmail: String(billTo.contactEmail ?? '').trim(),
    line1,
    city,
    state,
    zip,
    address: address || formatBillToAddress({ line1, city, state, zip }),
    notes: String(doc?.notes ?? '').trim(),
    poNumber: String(
      doc?.poNumber ?? fallbackDocFields?.poNumber ?? ''
    ).trim(),
  }
}

/**
 * Client-side validation before QB create/update — every bill-to field is required.
 * @param {Record<string, unknown>} docFields
 */
export function validateBillToFields(docFields) {
  if (!String(docFields?.orgName ?? '').trim()) {
    return 'Organization name is required'
  }
  if (!String(docFields?.contactName ?? '').trim()) {
    return 'Contact name is required'
  }
  if (!String(docFields?.contactEmail ?? '').trim()) {
    return 'Contact email is required for estimates and invoices'
  }
  if (!String(docFields?.line1 ?? docFields?.address ?? '').trim()) {
    return 'Street address is required'
  }
  if (!String(docFields?.city ?? '').trim()) return 'City is required'
  if (!String(docFields?.state ?? '').trim()) return 'State is required'
  if (!String(docFields?.zip ?? '').trim()) return 'ZIP code is required'
  return ''
}

/** @param {Record<string, unknown>} docFields */
export function isBillToComplete(docFields) {
  return validateBillToFields(docFields) === ''
}

/**
 * @param {Record<string, unknown>} customer
 * @param {Record<string, unknown> | null | undefined} [_org]
 */
export function resolveQuickbooksCustomerBillTo(customer, _org = null) {
  if (!customer || typeof customer !== 'object') return null
  const nested = customer.billTo
  if (nested && typeof nested === 'object') {
    const line1 = String(nested.line1 ?? nested.address ?? '').trim()
    if (line1 || String(nested.city ?? '').trim()) {
      return nested
    }
  }
  const line1 = String(customer.line1 ?? '').trim()
  if (line1 || String(customer.city ?? '').trim()) {
    return {
      orgName: String(customer.orgName ?? customer.displayName ?? '').trim(),
      contactName: String(customer.contactName ?? '').trim(),
      contactEmail: String(customer.contactEmail ?? '').trim(),
      line1,
      city: String(customer.city ?? '').trim(),
      state: String(customer.state ?? '').trim(),
      zip: String(customer.zip ?? '').trim(),
    }
  }
  return null
}

/**
 * Merge billTo from prior billing documents onto linked QB customers for display/selection.
 * @param {Array<Record<string, unknown>>} customers
 * @param {Array<Record<string, unknown>>} [billingDocuments]
 */
export function enrichQuickbooksCustomersWithBillTo(customers, billingDocuments = []) {
  const byId = new Map()
  for (const doc of billingDocuments) {
    const id = quickbooksCustomerIdFromDocument(doc)
    if (id && doc?.billTo && typeof doc.billTo === 'object') {
      byId.set(String(id), doc.billTo)
    }
  }
  return customers.map((customer) => {
    const id = String(customer.customerId ?? '')
    const fromDoc = id ? byId.get(id) : null
    const fromCustomer = resolveQuickbooksCustomerBillTo(customer)
    const billTo = fromCustomer ?? fromDoc ?? null
    return billTo ? { ...customer, billTo } : customer
  })
}

/**
 * Empty bill-to fields for "add new QuickBooks customer" checkout flow.
 * @param {Record<string, unknown>} [overrides]
 */
export function buildEmptyBillingDocFields(overrides = {}) {
  return {
    orgName: '',
    contactName: '',
    contactEmail: '',
    line1: '',
    city: '',
    state: '',
    zip: '',
    address: '',
    notes: '',
    poNumber: '',
    ...overrides,
  }
}

/**
 * Bill-to fields for a new QuickBooks customer — org name locked to the organization record.
 * @param {Record<string, unknown> | null | undefined} org
 * @param {Record<string, unknown>} [overrides]
 */
export function buildNewQuickbooksCustomerDocFields(org, overrides = {}) {
  return buildEmptyBillingDocFields({
    orgName: organizationBillToOrgName(org),
    ...overrides,
  })
}

/**
 * Map customer.billTo → portal docFields (QuickBooks picker only — no org/savedBillingInfo).
 * @param {Record<string, unknown>} customer
 */
export function docFieldsFromQuickbooksCustomerBillTo(customer, org = null) {
  const billTo = resolveQuickbooksCustomerBillTo(customer)
  if (!billTo) {
    return withOrganizationBillToOrgName(buildEmptyBillingDocFields(), org)
  }
  return withOrganizationBillToOrgName(billToFromDocument({ billTo }), org)
}

/**
 * @param {Record<string, unknown>} customer
 * @param {Record<string, unknown> | null | undefined} [_org] deprecated — ignored
 */
export function docFieldsFromQuickbooksCustomer(customer, _org = null) {
  return docFieldsFromQuickbooksCustomerBillTo(customer)
}

/**
 * @param {number} index zero-based index in the customer's linked list
 */
export function quickbooksBillingAddressLabel(index) {
  return `Billing address #${index + 1}`
}

/**
 * @param {Record<string, unknown>} fields
 * @returns {string[]}
 */
export function quickbooksCustomerAddressLines(fields) {
  const { line1, city, state, zip } = normalizeBillToAddressParts(fields ?? {})
  const cityStateZip = [city, state].filter(Boolean).join(', ')
  const tail = [cityStateZip, zip].filter(Boolean).join(' ')
  const lines = []
  if (line1) lines.push(line1)
  if (tail) lines.push(tail)
  return lines
}

/**
 * @param {Record<string, unknown>} customer
 * @param {number} [index] zero-based position in the org's linked billing addresses
 */
export function quickbooksCustomerDisplayLines(customer, index = 0) {
  const title =
    String(customer?.displayName ?? '').trim() || quickbooksBillingAddressLabel(index)
  const billTo = resolveQuickbooksCustomerBillTo(customer)
  if (!billTo) {
    return {
      title,
      addressLine: '',
      addressLines: [],
      addressMissing: true,
    }
  }
  const fields = billToFromDocument({ billTo }, {})
  const addressLines = quickbooksCustomerAddressLines(fields)
  return {
    title,
    addressLine: formatBillToAddress(fields),
    addressLines,
    addressMissing: addressLines.length === 0,
  }
}

/**
 * Address-only snapshot for QuickBooks billing-contact change detection.
 * @param {Record<string, unknown>} docFields
 */
export function snapshotBillToAddress(docFields) {
  const { line1, city, state, zip } = normalizeBillToAddressParts(docFields ?? {})
  return {
    line1,
    city,
    state: state.toUpperCase(),
    zip,
  }
}

/**
 * Address + contact snapshot when an existing QuickBooks customer is selected.
 * @param {Record<string, unknown>} docFields
 */
export function snapshotBillToForApproval(docFields) {
  return {
    ...snapshotBillToAddress(docFields),
    contactName: String(docFields?.contactName ?? '').trim(),
    contactEmail: String(docFields?.contactEmail ?? '').trim().toLowerCase(),
  }
}

/**
 * @param {Record<string, unknown>} fields
 */
export function formatBillToContact(fields) {
  const name = String(fields?.contactName ?? '').trim()
  const email = String(fields?.contactEmail ?? '').trim()
  if (name && email) return `${name} · ${email}`
  return name || email || '—'
}

/**
 * @param {Record<string, unknown>} current
 * @param {Record<string, unknown> | null | undefined} snapshot
 */
export function billToContactChanged(current, snapshot) {
  if (!snapshot) return false
  const a = snapshotBillToForApproval(current)
  const b = snapshotBillToForApproval(snapshot)
  return a.contactName !== b.contactName || a.contactEmail !== b.contactEmail
}

/**
 * @param {Record<string, unknown>} current
 * @param {Record<string, unknown> | null | undefined} snapshot
 */
export function billToAddressChanged(current, snapshot) {
  if (!snapshot) return false
  const a = snapshotBillToAddress(current)
  const b = snapshotBillToAddress(snapshot)
  return (
    a.line1 !== b.line1 ||
    a.city !== b.city ||
    a.state !== b.state ||
    a.zip !== b.zip
  )
}

/**
 * True when address or contact fields differ from the prefilled snapshot.
 * @param {Record<string, unknown>} current
 * @param {Record<string, unknown> | null | undefined} snapshot
 */
export function billToApprovalFieldsChanged(current, snapshot) {
  if (!snapshot) return false
  return billToAddressChanged(current, snapshot) || billToContactChanged(current, snapshot)
}

/** Shown when contact/address fields differ from the on-file snapshot (create + edit). */
export const BILLING_ADDRESS_APPROVAL_WARNING =
  'If you change the contact or address below and submit, RefReps will review your request. This document will use the billing details currently on file until approved.'

/** Extra line in the quote/invoice info callout when QB billing is active. */
export const BILLING_ADDRESS_APPROVAL_HINT =
  'Billing contact or address changes are submitted for RefReps approval and do not update on this document until approved.'

export const BILLING_ADDRESS_PENDING_TITLE = 'Billing change pending approval'

export const BILLING_ADDRESS_PENDING_BODY =
  'RefReps is reviewing your requested billing contact or address change. This document uses the details currently on file until approved.'

export const BILLING_ADDRESS_CONFIRM_LEAD_SAVE =
  'RefReps will review your requested changes. This document will continue to use the billing contact and address currently on file until approved.'

export const BILLING_ADDRESS_CONFIRM_LEAD_CREATE =
  'RefReps will review your requested changes. This document will be created using the billing contact and address currently on file until approved.'

/**
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function pendingBillingAddressChangeFromDocument(doc) {
  const req = doc?.billingAddressChangeRequest
  if (!req || typeof req !== 'object') return null
  if (String(req.status ?? '').toLowerCase() !== 'pending') return null
  return req
}

/**
 * On-file org name for an existing billing document (billTo.orgName).
 * @param {Record<string, unknown> | null | undefined} doc
 */
export function onFileBillToOrgNameFromDocument(doc) {
  if (!doc || typeof doc !== 'object') return ''
  const billTo = doc.billTo && typeof doc.billTo === 'object' ? doc.billTo : {}
  return String(billTo.orgName ?? '').trim()
}

/**
 * @param {Record<string, unknown>} docFields
 * @param {{ billingContactMode?: string, selectedQuickbooksCustomerId?: string | null }} [billingContact]
 */
export function buildBillToPayloadForSubmit(docFields, billingContact = {}) {
  const billTo = buildBillToPayload(docFields)

  if (billingContact.isExistingDocument) {
    billTo.orgName = String(
      billingContact.onFileOrgName ?? docFields?.orgName ?? ''
    ).trim()
  } else {
    const orgName = String(billingContact.organizationName ?? '').trim()
    if (orgName) {
      billTo.orgName = orgName
    }
  }

  if (!billingContact.quickbooksBillingContactActive) {
    return billTo
  }
  const mode = billingContact.billingContactMode
  const customerId = String(billingContact.selectedQuickbooksCustomerId ?? '').trim()

  if (mode === 'add_new') {
    billTo.addNewQuickbooksCustomer = true
    return billTo
  }
  if (mode === 'existing' && customerId) {
    billTo.quickbooksCustomerId = customerId
    billTo.addNewQuickbooksCustomer = false
    return billTo
  }
  return billTo
}

/**
 * @param {Record<string, unknown>} doc
 */
export function quickbooksCustomerIdFromDocument(doc) {
  if (!doc || typeof doc !== 'object') return ''
  const fromBillTo = doc.billTo?.quickbooksCustomerId
  if (fromBillTo != null && String(fromBillTo).trim()) {
    return String(fromBillTo).trim()
  }
  const fromNested = doc.quickbooks?.customerId
  if (fromNested != null && String(fromNested).trim()) {
    return String(fromNested).trim()
  }
  if (doc.quickbooksCustomerId != null && String(doc.quickbooksCustomerId).trim()) {
    return String(doc.quickbooksCustomerId).trim()
  }
  return ''
}

/** QB customers hidden from other orgs unless the org name matches exactly. */
export const RESTRICTED_QUICKBOOKS_CUSTOMER_NAMES = new Set([
  'IHSAA Foundation',
  'National Collegiate Athletic Association',
  'Jr. NBA',
])

/**
 * @param {Record<string, unknown> | null | undefined} customer
 */
export function quickbooksCustomerName(customer) {
  return String(customer?.displayName ?? customer?.orgName ?? '').trim()
}

/**
 * Whether a linked QB customer should appear in the billing-contact picker.
 * @param {Record<string, unknown> | null | undefined} customer
 * @param {string | undefined | null} organizationName
 */
export function isQuickbooksCustomerSelectableForOrganization(
  customer,
  organizationName
) {
  const customerName = quickbooksCustomerName(customer)
  if (!RESTRICTED_QUICKBOOKS_CUSTOMER_NAMES.has(customerName)) return true
  return String(organizationName ?? '').trim() === customerName
}

/**
 * @param {unknown} customers
 * @param {string | undefined | null} organizationName
 */
export function filterQuickbooksCustomersForOrganization(
  customers,
  organizationName
) {
  if (!Array.isArray(customers)) return []
  return customers.filter((customer) =>
    isQuickbooksCustomerSelectableForOrganization(customer, organizationName)
  )
}
